import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { fromJS } from 'immutable';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { matchPath } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { HelmetProvider } from 'react-helmet-async';
import history from '@client/shared/utils/history';
import { Auth0Provider } from '@client/helperComponents/react-auth0-spa';
import LazyLoadContext from '@client/helperComponents/context/LazyLoadContext';
import { cookiebotOnConsentReady } from '@client/shared/utils/cookiebot/cookiebot.js';
import { ai } from './shared/utils/telemetry/TelemetryService';
import configureStore from './redux/configureStore';
import AppFrame from './containers/appFrame/AppFrame';
import routes from './pages/routes';
import 'shared/styles/base.scss';
import { BASENAME } from '../shared/constants/router';

const config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientId: process.env.REACT_APP_AUTH0_CLIENTID,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  redirect_uri: process.env.REACT_APP_AUTH0_REDIRECT_URI,
};

let initialState = {};
if (typeof window === 'object' && window.__SERVER_STATE__) {
  try {
    let plain = window.__SERVER_STATE__;
    for (const key in plain) {
      if (key !== 'router' && key !== 'toastr') {
        initialState[key] = fromJS(plain[key]);
      }
    }
  } catch (error) {
    console.error('__SERVER_STATE__ error', error.toString());
  }
}

// Logging to Application Insights
ai.initialize(process.env.REACT_APP_INSTRUMENTATION_KEY, { history: history });

//cookiebot consent handling
cookiebotOnConsentReady();

const { store } = configureStore({
  initialState,
  history,
});

// Scroll to top on history change
history.listen(() => {
  if (typeof window === 'object') {
    window.scrollTo(0, 0);
  }
});

const lazyLoadContextValue = {
  injectReducer: store.injectReducer,
};

const supportsHistory = 'pushState' in window.history;

function collectMatched(routeList, pathname, accum) {
  if (!Array.isArray(routeList)) return accum;
  routeList.forEach((r) => {
    const path = r && r.path;
    if (!path) return;
    try {
      const match = matchPath(
        { path, end: !r.routes || r.routes.length === 0 },
        pathname
      );
      if (match) {
        accum.push(r);
        // If this route has children, attempt to match them against the remainder of the URL
        if (r.routes && r.routes.length) {
          const remainder =
            typeof match.pathnameBase === 'string' && pathname.startsWith(match.pathnameBase)
              ? pathname.slice(match.pathnameBase.length) || '/'
              : pathname;
          // Ensure leading slash for relative child paths
          const childPathname = remainder.startsWith('/') ? remainder : `/${remainder}`;
          collectMatched(r.routes, childPathname, accum);
        }
      }
    } catch (_) {}
  });
  return accum;
}

const prefetchMatchedRoutes = async (pathname) => {
  try {
    const base = BASENAME || '';
    const urlWithoutBase =
      pathname && pathname.startsWith(base)
        ? pathname.slice(base.length)
        : pathname;
    const matched = collectMatched(routes, urlWithoutBase || '/', []);
    const loaders = matched
      .map((r) => r && r.Component && r.Component.preload)
      .filter(Boolean)
      .map((preload) => preload());
    await Promise.all(loaders);
  } catch (_) {}
};

const renderApp = async (AppFrame) => {
  const container = document.getElementById('root');
  const currentPathname =
    typeof window === 'object' && window.location && window.location.pathname
      ? window.location.pathname
      : '';
  const detectedBasename = currentPathname.startsWith(BASENAME) ? BASENAME : '';
  await prefetchMatchedRoutes(currentPathname);
  const app = (
    <BrowserRouter basename={detectedBasename} forceRefresh={!supportsHistory}>
      <HelmetProvider>
        <Auth0Provider
          domain={config.domain}
          clientId={config.clientId}
          authorizationParams={{
            audience: config.audience,
            redirect_uri: config.redirect_uri,
            scope: 'openid profile email',
          }}
          dispatch={store.dispatch}
          legacySameSiteCookie={false}
        >
          <LazyLoadContext.Provider value={lazyLoadContextValue}>
            <Provider store={store}>
              <CookiesProvider>
                <AppFrame />
              </CookiesProvider>
            </Provider>
          </LazyLoadContext.Provider>
        </Auth0Provider>
      </HelmetProvider>
    </BrowserRouter>
  );

  hydrateRoot(container, app, {
    onRecoverableError: (error, info) => {
      try {
        // eslint-disable-next-line no-console
        console.error('[HYDRATION]', error && (error.stack || error), info);
      } catch (_) {}
    },
  });
};

renderApp(AppFrame);

if (process.env.NODE_ENV === 'development' && module.hot) {
  module.hot.accept('./containers/appFrame/AppFrame.js', () => {
    const App = require('./containers/appFrame/AppFrame').default; // eslint-ignore-line
    renderApp(App);
  });
}
